// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import cs from 'classnames';
import { colorIconSelector, ColorOptionType, getColorOption, IdToColor } from 'components/common/ColorOptions';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ParamsWithId from 'model/ParamsWithId';
import { useParams } from 'react-router-dom';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import GetOS, { OS } from 'components/common/GetOs';
import { Collapsible, Table } from '@tls/treact-ui';
import useGlobalKeyboardEvent from 'hooks/useGlobalKeyboardEvent';

export default function LabelSelection() {
  const { t } = useTranslation();
  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);

  const modelClassesWithoutBackground = useMemo(() => {
    return selectedProject?.projectUseCase?.modelClasses?.filter(modelClass => modelClass.classIndex !== 0);
  }, [selectedProject?.projectUseCase?.modelClasses]);

  const colorOptions = useMemo(() => {
    return modelClassesWithoutBackground?.map(modelClass => getColorOption(modelClass.labelColor)) ?? [];
  }, [modelClassesWithoutBackground]);

  const [color, setColor] = useState<ColorOptionType>(() => colorOptions[0]);
  const labelsArray = useMemo(() => colorOptions.map(color => IdToColor(color.id)), [colorOptions]);

  useEffect(() => {
    if (colorOptions.length > 0) {
      setColor(colorOptions[0]);
      document.dispatchEvent(new CustomEvent('labelindexchanged', { bubbles: true, detail: { idx: 0 } }));
    }
  }, [colorOptions]);

  const labelInfo = useCallback(
    (colorId: string) => {
      const colorString = IdToColor(colorId);
      const matchingClass = modelClassesWithoutBackground?.find(modelClass => {
        const labelColor = modelClass.labelColor;
        return (
          labelColor?.red === colorString.red &&
          labelColor?.green === colorString.green &&
          labelColor?.blue === colorString.blue
        );
      });
      return {
        labelName: matchingClass?.className,
        labelCategory: /*matchingClass?.labelCategory ?? */ 'Label Category',
      };
    },
    [modelClassesWithoutBackground]
  );

  const handleColorChange = useCallback(
    (selectedColor: ColorOptionType) => {
      setColor(selectedColor);
      document.dispatchEvent(
        new CustomEvent('labelindexchanged', { bubbles: true, detail: { idx: colorOptions.indexOf(selectedColor) } })
      );
    },
    [colorOptions]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      const colorIndex = colorOptions.indexOf(color);
      const { key, metaKey, ctrlKey } = event;

      const os = GetOS();
      const IsCtrlPressed = os === OS.MacOs ? metaKey : ctrlKey;
      if (key.toLowerCase() === 'w') {
        if (colorIndex > 0) {
          handleColorChange(colorOptions[colorIndex - 1]);
        }
      } else if (key.toLowerCase() === 's' && !IsCtrlPressed) {
        if (colorIndex < colorOptions.length - 1) {
          handleColorChange(colorOptions[colorIndex + 1]);
        }
      }
    },
    [handleColorChange, color, colorOptions]
  );

  useGlobalKeyboardEvent('keydown', handleKeyPress);

  useEffect(() => {
    document.dispatchEvent(new CustomEvent('labelschanged', { bubbles: true, detail: { labels: labelsArray } }));
  }, [labelsArray]);

  return (
    <Container>
      <Collapsible defaultOpen label={<BoldLabel>{t('project.label.tools.labels')}</BoldLabel>}>
        <Table>
          <Table.Body>
            {colorOptions.map(colorOption => (
              <Table.Row
                className={cs({
                  selected: colorOption?.id === color?.id,
                })}
                key={colorOption?.id}
                onClick={() => handleColorChange(colorOption)}
                data-testid={`label-selection-label-${colorOption.id}-row`}
              >
                <Table.Cell width={'3.5rem'} onClick={() => handleColorChange(colorOption)}>
                  <ColorIcon>{colorIconSelector(colorOption)}</ColorIcon>
                </Table.Cell>
                <Table.Cell width={1} onClick={() => handleColorChange(colorOption)}>
                  <ColorLabel>{labelInfo(colorOption.id).labelName}</ColorLabel>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Collapsible>
    </Container>
  );
}

const Container = styled.div`
  padding: 0.5rem;
  width: calc(100%-1rem);
  overflow: hidden;
`;

const BoldLabel = styled.span`
  font-weight: bold;
`;

const ColorIcon = styled.div`
  margin-right: 0.5rem;
`;
const ColorLabel = styled.div`
  font-size: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;
